import { useEffect, useMemo } from 'react'

import { useGetStopsHeatMapQuery } from 'api/accounts/accountRouting'
import { useLazyGetAccountPaymentsQuery } from 'api/accounts/getAccountPayments'
import { useLazyGetVacationHoldListQuery } from 'api/accounts/vacationHoldsCrud'
import { useFetchWorkOrderFacetsQuery } from 'api/work-order/fetchWorkOrderFacets'
import { UNASSIGNED_ROUTE } from 'containers/new-route-manager/settings'

import { WORK_ORDER_STATUS_FILTER_MENU_OPTIONS, WORK_ORDER_STATUS_IDS } from '../work-orders/filters/work-order-status-filter/settings'
import { isEverythingSelected } from '../work-orders/utils'

import { toRouteDayMeta, type RouteDayMeta } from './routeDayMeta'
import { useCalendarDateWindow } from './useCalendarDateWindow'

import type { WorkOrderDateFacetApi, WorkOrderFacetsRequestApi } from 'types/api/work-order/workOrderFacets'

interface FilterOption {
  id: string
  value?: string
}

interface Params {
  accountId?: string
  /** Set when the caller renders nothing, so a failure cannot land where no surface reports it. */
  skip?: boolean
  locationId?: string
  selectedRoutes?: FilterOption[]
  selectedServices?: FilterOption[]
  selectedWorkOrderStatuses?: FilterOption[]
  routesOptions?: FilterOption[]
  servicesOptions?: FilterOption[]
}

const EMPTY_BY_DATE: WorkOrderDateFacetApi[] = []

/**
 * Single hook that owns all calendar data fetching and shared transformations.
 * Called once in ServicesContentPanel; results are passed as props to Routing and EventsCalendar.
 */
export const useCalendarData = ({
  accountId,
  skip = false,
  locationId,
  selectedRoutes,
  selectedServices,
  selectedWorkOrderStatuses,
  routesOptions,
  servicesOptions,
}: Params) => {
  const { windowStartDate, windowEndDate, expandWindowIfNeeded, ensureDateInWindow } = useCalendarDateWindow()

  const {
    data: heatmapData,
    isLoading: isLoadingHeatmap,
    isFetching: isFetchingHeatmap,
  } = useGetStopsHeatMapQuery({ accountId, startDate: windowStartDate, endDate: windowEndDate }, { skip: skip || !accountId })

  // An empty selection means "show nothing", the way the client-side filter used to — never "no filter".
  const isAnyFilterCleared =
    (servicesOptions?.length ?? 0) > 0 &&
    (selectedServices?.length === 0 || selectedRoutes?.length === 0 || selectedWorkOrderStatuses?.length === 0)

  const facetsRequest = useMemo<WorkOrderFacetsRequestApi>(() => {
    const request: WorkOrderFacetsRequestApi = {
      accountId: accountId as string,
      ...(locationId && { locationId }),
      startDate: windowStartDate,
      endDate: windowEndDate,
    }

    if (!isEverythingSelected(selectedRoutes, routesOptions?.length ?? 0)) {
      const selected = selectedRoutes ?? []
      request.routeIds = selected.filter(route => route.id !== UNASSIGNED_ROUTE.id).map(route => route.id)
      request.includeUnassigned = selected.some(route => route.id === UNASSIGNED_ROUTE.id)
    }

    if (!isEverythingSelected(selectedServices, servicesOptions?.length ?? 0)) {
      request.pricedServiceIds = (selectedServices ?? []).map(service => service.id)
    }

    // Always sent, the way the work order list does: omitted, the SQL counts statuses nothing displays.
    request.statuses = isEverythingSelected(selectedWorkOrderStatuses, WORK_ORDER_STATUS_FILTER_MENU_OPTIONS.length)
      ? WORK_ORDER_STATUS_IDS
      : (selectedWorkOrderStatuses ?? []).map(status => status.id)

    return request
  }, [
    accountId,
    locationId,
    windowStartDate,
    windowEndDate,
    selectedRoutes,
    selectedServices,
    selectedWorkOrderStatuses,
    routesOptions,
    servicesOptions,
  ])

  const {
    data: facetsData,
    isLoading: isLoadingWorkOrders,
    isFetching: isFetchingWorkOrders,
    isSuccess: isWorkOrdersSuccess,
    isError: isWorkOrdersError,
    error: workOrdersError,
  } = useFetchWorkOrderFacetsQuery(facetsRequest, { skip: skip || !accountId || isAnyFilterCleared })

  const [getVacationHoldList, { data: vacationHoldData, isLoading: isLoadingVacationHolds }] = useLazyGetVacationHoldListQuery()

  const [getAccountPayments, { data: paymentData, isLoading: isLoadingPayments }] = useLazyGetAccountPaymentsQuery()

  useEffect(() => {
    if (skip || !accountId) return
    getVacationHoldList({ accountId, deleted: false })
  }, [skip, accountId, getVacationHoldList])

  useEffect(() => {
    if (skip || !accountId) return
    getAccountPayments({ accountId })
  }, [skip, accountId, getAccountPayments])

  const apiHeatmap = heatmapData?.heatmap ?? {}
  const byDate = isAnyFilterCleared ? EMPTY_BY_DATE : facetsData?.byDate ?? EMPTY_BY_DATE

  const routingHeatmap = useMemo(() => {
    const heatmap: Record<string, { recurringCount: number; requestCount: number }> = {}
    byDate.forEach(row => {
      heatmap[row.serviceDate] = { recurringCount: row.recurringCount, requestCount: row.requestCount }
    })
    return heatmap
  }, [byDate])

  const routesByDate = useMemo(() => {
    const grouped: Record<string, RouteDayMeta> = {}
    byDate.forEach(row => {
      grouped[row.serviceDate] = toRouteDayMeta(row)
    })
    return grouped
  }, [byDate])

  const paymentsByDate = useMemo(() => {
    const grouped: Record<string, number> = {}
    const payments = paymentData?.payments ?? []
    payments.forEach((payment: { paymentDate?: string }) => {
      if (payment?.paymentDate) {
        grouped[payment.paymentDate] = (grouped[payment.paymentDate] ?? 0) + 1
      }
    })
    return grouped
  }, [paymentData])

  const isLoading =
    isLoadingHeatmap || isLoadingWorkOrders || isFetchingHeatmap || isFetchingWorkOrders || isLoadingVacationHolds || isLoadingPayments
  const isFetched = isWorkOrdersSuccess || isWorkOrdersError || isAnyFilterCleared

  return {
    expandWindowIfNeeded,
    ensureDateInWindow,
    isLoading,
    isFetched,
    // isFetched goes true on failure too, so a caller that ignores this renders an empty calendar
    // for a failed request.
    isError: isWorkOrdersError,
    error: workOrdersError,

    routingHeatmap,
    routesByDate,

    apiHeatmap,
    byDate,
    vacationHoldData,
    paymentsByDate,
  }
}
